var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useCallback, useEffect, useRef, useState } from "react";
import config from "../config";
import { isUsageLimitBody, reportUsageLimit } from "../services/usageLimits";
import { useAuth } from "../contexts/AuthContext";
import api from "../services/api";
const DEBOUNCE_MS = 1500;
const FIRST_DEBOUNCE_MS = 300;
export function useSearchSession(filters, onCorrectFilters, target = "sales-nav") {
    const { token } = useAuth();
    const [state, setState] = useState({
        sessionId: null,
        isConnecting: false,
        isReady: false,
        resultCount: null,
        isApplyingFilters: false,
        isScraping: false,
        results: [],
        totalResults: null,
        currentPage: 1,
        error: null,
        failedFilters: null,
        hasUnsearchedFilters: false,
        autocompleteSuggestions: null,
        isFetchingAutocomplete: false,
    });
    const eventSourceRef = useRef(null);
    const reconnectTimerRef = useRef(null);
    const isOpeningStreamRef = useRef(false);
    const debounceTimerRef = useRef(null);
    const lastAppliedFiltersRef = useRef(JSON.stringify(filters));
    const hasAppliedFiltersRef = useRef(false);
    const sessionIdRef = useRef(null);
    const pageCacheRef = useRef({});
    const pendingFiltersRef = useRef(null);
    const filterInFlightRef = useRef(false);
    const filterResultResolverRef = useRef(null);
    const onCorrectFiltersRef = useRef(onCorrectFilters);
    onCorrectFiltersRef.current = onCorrectFilters;
    const exportingRef = useRef(false);
    const cancelledRef = useRef(false);
    // --- SSE event handler ---
    const handleSSEEvent = useCallback((data) => {
        var _a, _b, _c;
        switch (data.type) {
            case "session-ready":
                setState((prev) => (Object.assign(Object.assign({}, prev), { isReady: true, isConnecting: false })));
                break;
            case "applying-filters":
                setState((prev) => (Object.assign(Object.assign({}, prev), { isApplyingFilters: true })));
                break;
            case "filter-result":
                pageCacheRef.current = {};
                filterInFlightRef.current = false;
                if (filterResultResolverRef.current) {
                    filterResultResolverRef.current();
                    filterResultResolverRef.current = null;
                }
                setState((prev) => {
                    var _a, _b;
                    return (Object.assign(Object.assign({}, prev), { isApplyingFilters: false, resultCount: (_a = data.resultCount) !== null && _a !== void 0 ? _a : null, failedFilters: (_b = data.failedFilters) !== null && _b !== void 0 ? _b : prev.failedFilters }));
                });
                if (data.correctedValues && onCorrectFiltersRef.current) {
                    onCorrectFiltersRef.current(data.correctedValues);
                }
                break;
            case "autocomplete-result":
                setState((prev) => {
                    var _a;
                    return (Object.assign(Object.assign({}, prev), { isFetchingAutocomplete: false, autocompleteSuggestions: {
                            filterType: data.filterType,
                            query: data.query,
                            suggestions: (_a = data.suggestions) !== null && _a !== void 0 ? _a : [],
                        } }));
                });
                break;
            case "scraping":
                // Results are dropped here, not on arrival: the list renders as soon as the first
                // streamed rows land, so leaving the previous search's rows up would pass them off
                // as results for the new one.
                setState((prev) => (Object.assign(Object.assign({}, prev), { isScraping: true, results: [] })));
                break;
            // Rows the extension has rendered so far. The scrape stays in progress; the list is
            // replaced wholesale when scrape-result lands.
            case "scrape-progress": {
                const results = (_a = data.results) !== null && _a !== void 0 ? _a : [];
                if (results.length === 0)
                    break;
                setState((prev) => (Object.assign(Object.assign({}, prev), { results })));
                break;
            }
            case "scrape-result": {
                const page = (_b = data.page) !== null && _b !== void 0 ? _b : 1;
                const results = (_c = data.results) !== null && _c !== void 0 ? _c : [];
                // Profiles past today's Ready For Change limit (or our capacity) are withheld from the page
                if (isUsageLimitBody(data.limitReached))
                    reportUsageLimit(data.limitReached);
                pageCacheRef.current[page] = results;
                setState((prev) => {
                    var _a, _b, _c;
                    return (Object.assign(Object.assign({}, prev), { isScraping: false, results, totalResults: (_a = data.totalResults) !== null && _a !== void 0 ? _a : null, currentPage: page, error: (_b = data.error) !== null && _b !== void 0 ? _b : null, failedFilters: (_c = data.failedFilters) !== null && _c !== void 0 ? _c : prev.failedFilters, hasUnsearchedFilters: false }));
                });
                break;
            }
            case "session-closed":
                setState((prev) => (Object.assign(Object.assign({}, prev), { isConnecting: false, isReady: false, error: "Session was closed. Please refresh to start a new session." })));
                break;
            case "error":
                setState((prev) => {
                    var _a;
                    return (Object.assign(Object.assign({}, prev), { isConnecting: false, isReady: false, error: (_a = data.message) !== null && _a !== void 0 ? _a : "Session error" }));
                });
                break;
        }
    }, []);
    // --- SSE stream opener (stable across renders) ---
    const openSSEStream = useCallback((sessionId) => __awaiter(this, void 0, void 0, function* () {
        if (!token || eventSourceRef.current || isOpeningStreamRef.current)
            return;
        isOpeningStreamRef.current = true;
        try {
            const ticketRes = yield api.createSearchSessionStreamTicket(token, sessionId);
            if (cancelledRef.current)
                return;
            if (!ticketRes.success) {
                console.warn("[useSearchSession] Ticket fetch returned failure, scheduling retry");
                if (reconnectTimerRef.current)
                    clearTimeout(reconnectTimerRef.current);
                reconnectTimerRef.current = setTimeout(() => {
                    reconnectTimerRef.current = null;
                    if (!cancelledRef.current && sessionIdRef.current)
                        openSSEStream(sessionIdRef.current);
                }, 3000);
                return;
            }
            if (eventSourceRef.current)
                return;
            const url = `${config.API_URL}/search-session/${sessionId}/stream?ticket=${encodeURIComponent(ticketRes.data.ticket)}`;
            const es = new EventSource(url);
            eventSourceRef.current = es;
            es.onmessage = (event) => {
                try {
                    const data = JSON.parse(event.data);
                    if (data.type !== "heartbeat" && data.type !== "connected") {
                        handleSSEEvent(data);
                    }
                }
                catch (_a) {
                    // Ignore parse errors
                }
            };
            es.onerror = () => {
                console.warn("[useSearchSession] SSE error, will attempt reconnect");
                es.close();
                eventSourceRef.current = null;
                if (reconnectTimerRef.current)
                    clearTimeout(reconnectTimerRef.current);
                if (!cancelledRef.current && sessionIdRef.current) {
                    reconnectTimerRef.current = setTimeout(() => {
                        reconnectTimerRef.current = null;
                        if (!cancelledRef.current && sessionIdRef.current) {
                            openSSEStream(sessionIdRef.current);
                        }
                    }, 3000);
                }
            };
        }
        finally {
            isOpeningStreamRef.current = false;
        }
    }), [token, handleSSEEvent]);
    // --- Manual connection trigger ---
    const startConnection = useCallback(() => __awaiter(this, void 0, void 0, function* () {
        if (!token || sessionIdRef.current)
            return;
        cancelledRef.current = false;
        setState((prev) => (Object.assign(Object.assign({}, prev), { isConnecting: true, error: null })));
        const MAX_CREATE_RETRIES = 3;
        const connect = (attempt = 1) => __awaiter(this, void 0, void 0, function* () {
            try {
                const sessionRes = yield api.createSearchSession(token, target);
                if (cancelledRef.current)
                    return;
                if (!sessionRes.success) {
                    if (attempt < MAX_CREATE_RETRIES) {
                        console.warn(`[useSearchSession] Session creation failed (attempt ${attempt}/${MAX_CREATE_RETRIES}), retrying`);
                        if (reconnectTimerRef.current)
                            clearTimeout(reconnectTimerRef.current);
                        reconnectTimerRef.current = setTimeout(() => {
                            reconnectTimerRef.current = null;
                            if (!cancelledRef.current)
                                connect(attempt + 1);
                        }, 3000 * attempt);
                        return;
                    }
                    setState((prev) => (Object.assign(Object.assign({}, prev), { isConnecting: false, error: "Failed to create search session" })));
                    return;
                }
                const sessionId = sessionRes.data.id;
                sessionIdRef.current = sessionId;
                setState((prev) => (Object.assign(Object.assign({}, prev), { sessionId })));
                yield openSSEStream(sessionId);
            }
            catch (err) {
                if (cancelledRef.current)
                    return;
                console.error(`[useSearchSession] Session connect failed (attempt ${attempt}/${MAX_CREATE_RETRIES}):`, err);
                if (attempt < MAX_CREATE_RETRIES) {
                    if (reconnectTimerRef.current)
                        clearTimeout(reconnectTimerRef.current);
                    reconnectTimerRef.current = setTimeout(() => {
                        reconnectTimerRef.current = null;
                        if (!cancelledRef.current)
                            connect(attempt + 1);
                    }, 3000 * attempt);
                }
                else {
                    const reason = err instanceof Error ? err.message : "";
                    setState((prev) => (Object.assign(Object.assign({}, prev), { isConnecting: false, error: reason ? `Couldn't start a search session: ${reason}` : "Failed to connect to search session" })));
                }
            }
        });
        yield connect();
    }), [token, target, openSSEStream]);
    // --- Cleanup on unmount ---
    useEffect(() => {
        return () => {
            cancelledRef.current = true;
            if (reconnectTimerRef.current) {
                clearTimeout(reconnectTimerRef.current);
                reconnectTimerRef.current = null;
            }
            if (eventSourceRef.current) {
                eventSourceRef.current.close();
                eventSourceRef.current = null;
            }
            if (sessionIdRef.current && token && !exportingRef.current) {
                api.closeSearchSession(token, sessionIdRef.current);
            }
        };
    }, [token]);
    // --- Debounced filter application ---
    const targetRef = useRef(target);
    targetRef.current = target;
    useEffect(() => {
        if (!(state.isReady && state.sessionId && token))
            return;
        const filtersJson = JSON.stringify(filters) + target;
        if (lastAppliedFiltersRef.current === filtersJson) {
            pendingFiltersRef.current = null;
            return;
        }
        pendingFiltersRef.current = filters;
        pageCacheRef.current = {};
        if (target === "recruiter") {
            setState((prev) => (prev.hasUnsearchedFilters ? prev : Object.assign(Object.assign({}, prev), { hasUnsearchedFilters: true })));
        }
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }
        const debounceMs = hasAppliedFiltersRef.current ? DEBOUNCE_MS : FIRST_DEBOUNCE_MS;
        debounceTimerRef.current = setTimeout(() => __awaiter(this, void 0, void 0, function* () {
            hasAppliedFiltersRef.current = true;
            lastAppliedFiltersRef.current = filtersJson;
            pendingFiltersRef.current = null;
            filterInFlightRef.current = true;
            yield api.applySessionFilters(token, state.sessionId, filters, targetRef.current);
        }), debounceMs);
        return () => {
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }
        };
    }, [filters, target, state.isReady, state.sessionId, token]);
    // --- Search action ---
    const startSearch = useCallback((page = 1) => __awaiter(this, void 0, void 0, function* () {
        if (!(state.sessionId && token))
            return;
        // Return cached results if this page was already scraped
        const cached = pageCacheRef.current[page];
        if (cached) {
            setState((prev) => (Object.assign(Object.assign({}, prev), { results: cached, currentPage: page })));
            return;
        }
        // Immediate feedback so the UI shows "Processing search..." until the first rows stream in
        setState((prev) => (Object.assign(Object.assign({}, prev), { isScraping: true, results: [] })));
        try {
            // Flush any pending debounced filter changes before searching.
            // This ensures the extension receives apply-filters BEFORE start-scrape
            // and its queue executes them in the correct order.
            if (pendingFiltersRef.current) {
                if (debounceTimerRef.current) {
                    clearTimeout(debounceTimerRef.current);
                    debounceTimerRef.current = null;
                }
                const pendingFilters = pendingFiltersRef.current;
                pendingFiltersRef.current = null;
                hasAppliedFiltersRef.current = true;
                lastAppliedFiltersRef.current = JSON.stringify(pendingFilters);
                filterInFlightRef.current = true;
                yield api.applySessionFilters(token, state.sessionId, pendingFilters, targetRef.current);
            }
            // Wait for the extension to finish applying filters on LinkedIn before
            // starting the scrape. Without this, a search fired while the extension
            // is still clicking filter options would scrape stale results.
            if (filterInFlightRef.current) {
                yield new Promise((resolve) => {
                    const timeout = setTimeout(() => {
                        filterResultResolverRef.current = null;
                        resolve();
                    }, 30000);
                    filterResultResolverRef.current = () => {
                        clearTimeout(timeout);
                        resolve();
                    };
                });
            }
            yield api.startSessionSearch(token, state.sessionId, page);
        }
        catch (_a) {
            setState((prev) => (Object.assign(Object.assign({}, prev), { isScraping: false, error: "Search failed. Please try again." })));
        }
    }), [state.sessionId, token]);
    const markExporting = useCallback(() => {
        exportingRef.current = true;
    }, []);
    const fetchAutocomplete = useCallback((filterType, query) => __awaiter(this, void 0, void 0, function* () {
        if (!(state.sessionId && token))
            return;
        setState((prev) => (Object.assign(Object.assign({}, prev), { isFetchingAutocomplete: true, autocompleteSuggestions: null })));
        yield api.fetchAutocomplete(token, state.sessionId, filterType, query);
    }), [state.sessionId, token]);
    const clearAutocompleteSuggestions = useCallback(() => {
        setState((prev) => (Object.assign(Object.assign({}, prev), { autocompleteSuggestions: null, isFetchingAutocomplete: false })));
    }, []);
    const clearFailedFilters = useCallback(() => {
        setState((prev) => (Object.assign(Object.assign({}, prev), { failedFilters: null })));
    }, []);
    return Object.assign(Object.assign({}, state), { startConnection,
        startSearch,
        markExporting,
        fetchAutocomplete,
        clearAutocompleteSuggestions,
        clearFailedFilters });
}
